import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";

// Replace with your actual Stripe publishable key
const STRIPE_PUBLISHABLE_KEY =
  "pk_live_51RXXj3GMpUo0559ksChy3Ms8lbhhTroFewhKJMzEF5B4TPM4fZ7fjpBWGns2rGE9znSzdfmKaT2qOBtZ8f3n1Xo300UEweCbjs";
const API_URL = process.env.REACT_APP_API_STRIPE_URL || "http://localhost:3001";

// Initialize Stripe
let stripePromise;
export const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

// Create checkout session directly with Stripe
export const createCheckoutSession = async (priceId, userId, email) => {
  const stripe = await getStripe();

  try {
    // Use Stripe Checkout directly from client-side
    const { error } = await stripe.redirectToCheckout({
      mode: "subscription",
      lineItems: [{ price: priceId, quantity: 1 }],
      successUrl: `${window.location.origin}/groupleader/subscription-success?session_id={CHECKOUT_SESSION_ID}&userId=${userId}`,
      cancelUrl: `${window.location.origin}/groupleader/groupleaderdashboard`,
      customerEmail: email,
      clientReferenceId: userId.toString(), // To identify the customer in Stripe webhooks
    });

    if (error) {
      console.error("Error redirecting to checkout:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error("Error creating checkout session:", err);
    return { success: false, error: err.message };
  }
};

// Create checkout session for metered billing (requires backend)
export const createMeteredBillingCheckoutSession = async (
  priceId,
  userId,
  email,
  groupId
) => {
  try {
    console.log("Creating metered billing checkout session", {
      priceId,
      userId,
    });

    // For metered billing, we MUST use the backend to create the checkout session
    const response = await axios.post(
      `${API_URL}/api/stripe/create-metered-checkout-session`,
      {
        priceId,
        userId,
        email,
        // The key issue: we need to include the # in the redirect URLs
        // for hash router mode to work, but we don't want Stripe to encode it
        successUrl: `${window.location.origin}/#/groupleader/subscription-success?session_id={CHECKOUT_SESSION_ID}&userId=${userId}&price_id=${priceId}`,
        cancelUrl: `${window.location.origin}/#/groupleader/groupleaderdashboard`,
        groupId: groupId.toString(),
      }
    );

    if (!response.data.success) {
      throw new Error(
        response.data.message || "Failed to create checkout session"
      );
    }

    // Get the checkout URL directly from the API response
    if (response.data.url) {
      // Redirect directly using the URL provided by the backend
      console.log("Redirecting to Stripe URL:", response.data.url);
      window.location.replace(response.data.url); // Use replace to avoid adding to browser history
    } else if (response.data.sessionId) {
      console.log(
        "No direct URL provided, using sessionId:",
        response.data.sessionId
      );
      // If no URL but we have a sessionId, construct the URL
      window.location.replace(
        `https://checkout.stripe.com/pay/${response.data.sessionId}`
      );
    } else {
      throw new Error("No valid redirect URL or session ID returned from API");
    }

    return { success: true };
  } catch (err) {
    console.error("Error creating metered billing checkout session:", err);
    return { success: false, error: err.message };
  }
};

// Report usage for metered billing
export const reportMeteredUsage = async (
  customerId,
  supportTeamCount,
  priceId
) => {
  try {
    console.log("Reporting metered usage:", {
      customerId,
      supportTeamCount,
      priceId,
    });

    // We need to report the usage through our backend API since it requires the secret key
    const response = await axios.post(
      `${API_URL}/api/stripe/report-metered-usage`,
      {
        customerId,
        supportTeamCount,
        priceId: priceId || "price_1RePgb4cIBSvP5cXkOJs8MPP",
      }
    );

    return {
      success: response.data.success,
      message: response.data.message,
    };
  } catch (err) {
    console.error("Error reporting metered usage:", err);
    return { success: false, error: err.message };
  }
};

// Check subscription status with Stripe
// Note: We still need a backend endpoint for this due to security
// Stripe doesn't allow direct customer lookup from client side with publishable key
export const checkSubscriptionStatus = async (email) => {
  try {
    const response = await axios.get(
      `${API_URL}/api/stripe/check-subscription?email=${encodeURIComponent(
        email
      )}`
    );
    return {
      hasActiveSubscription: response.data.hasActiveSubscription,
      subscriptionDetails: response.data.subscriptionDetails || null,
    };
  } catch (err) {
    console.error("Error checking subscription status:", err);
    return {
      hasActiveSubscription: false,
      error: err.message,
    };
  }
};

// Check subscription status by groupId
export const checkGroupSubscriptionStatus = async (groupId) => {
  try {
    const response = await axios.get(
      `https://jin-stripe-bmbueparbvgmasaa.eastus-01.azurewebsites.net/api/stripe/subscription-status?groupId=${groupId}`
    );
    return {
      success: true,
      hasActiveSubscription: response.data.hasActiveSubscription,
      subscriptionDetails: response.data.subscription || null,
    };
  } catch (err) {
    console.error("Error checking group subscription status:", err);
    return {
      success: false,
      hasActiveSubscription: false,
      error: err.message,
    };
  }
};

// Create customer portal session directly with Stripe
export const createBillingPortalSession = async (customerId) => {
  try {
    // Note: Customer Portal needs to be created through the backend
    // due to required secret key
    const response = await axios.post(
      `${API_URL}/api/stripe/create-billing-portal`,
      {
        customerId,
        returnUrl: `${window.location.origin}/groupleader/groupleaderdashboard`,
      }
    );

    if (response.data.url) {
      window.location.href = response.data.url;
      return { success: true };
    } else {
      throw new Error("No portal URL returned");
    }
  } catch (err) {
    console.error("Error creating billing portal session:", err);
    return { success: false, error: err.message };
  }
};

// Retrieve a session to check its status
export const retrieveCheckoutSession = async (sessionId) => {
  const stripe = await getStripe();
  try {
    const session = await stripe.checkout.sessions.retrieve(sessionId);
    return { success: true, session };
  } catch (err) {
    console.error("Error retrieving session:", err);
    return { success: false, error: err.message };
  }
};

// Create checkout session for one-time job posting payment
export const createJobPostingCheckoutSession = async (userId, email) => {
  try {
    console.log("Creating job posting checkout session", { userId, email });

    const response = await axios.post(
      `${API_URL}/api/stripe/create-job-checkout-session`,
      {
        productId: "prod_SjJWop9rienWAE",
        userId,
        email,
        successUrl: `${window.location.origin}/#/employer/job-payment-success?session_id={CHECKOUT_SESSION_ID}&userId=${userId}`,
        cancelUrl: `${window.location.origin}/#/postjob`,
      }
    );

    if (!response.data.success) {
      throw new Error(
        response.data.message || "Failed to create checkout session"
      );
    }

    // Redirect to Stripe Checkout
    if (response.data.url) {
      console.log("Redirecting to Stripe URL:", response.data.url);
      window.location.replace(response.data.url);
    } else {
      throw new Error("No valid redirect URL returned from API");
    }

    return { success: true };
  } catch (err) {
    console.error("Error creating job posting checkout session:", err);
    return { success: false, error: err.message };
  }
};

// Verify job posting payment status
export const verifyJobPostingPayment = async (sessionId, userId) => {
  try {
    const response = await axios.get(
      `${API_URL}/api/stripe/verify-job-payment?sessionId=${sessionId}&userId=${userId}`
    );
    return {
      success: response.data.success,
      paymentCompleted: response.data.paymentCompleted,
      sessionId: response.data.sessionId,
      paymentIntentId: response.data.paymentIntentId,
    };
  } catch (err) {
    console.error("Error verifying job posting payment:", err);
    return {
      success: false,
      paymentCompleted: false,
      error: err.message,
    };
  }
};

// Check if user has recent job posting payment
export const checkUserJobPaymentHistory = async (userId) => {
  try {
    const response = await axios.get(
      `${API_URL}/api/stripe/user-job-payments?userId=${userId}`
    );
    return {
      success: response.data.success,
      hasRecentPayment: response.data.hasRecentPayment,
      payments: response.data.payments || [],
    };
  } catch (err) {
    console.error("Error checking user payment history:", err);
    return {
      success: false,
      hasRecentPayment: false,
      error: err.message,
    };
  }
};
